/**
 * Meta Pixel initialization and management utility.
 * Initializes the Facebook Pixel script and provides methods for tracking events.
 * The pixel automatically sets the _fbp cookie which is captured by attribution tracking.
 * Update when modifying Meta Pixel configuration or adding new event tracking methods.
 */

type MetaPixelCommand = [command: string, ...args: unknown[]];

type MetaPixelFunction = ((...args: MetaPixelCommand) => void) & {
  callMethod?: (...args: MetaPixelCommand) => void;
  loaded?: boolean;
  l?: number;
  push?: MetaPixelFunction;
  queue?: MetaPixelCommand[];
  q?: MetaPixelCommand[];
  version?: string;
  instance?: {
    pixelsByID?: Record<string, unknown>;
  };
  pendingConfigs?: Record<string, unknown>;
  _initsDone?: Record<string, unknown>;
};

declare global {
  interface Window {
    _fbq?: MetaPixelFunction;
    _metaPixelErrorHandlerAdded?: boolean;
    _metaPixelInitialized?: boolean;
    fbq?: MetaPixelFunction;
  }
}

const META_PIXEL_ALLOWED_HOSTS = new Set(['deepthink.sg', 'www.deepthink.sg']);

const createMetaPixelQueueStub = (): MetaPixelFunction => {
  const fbq = ((...args: MetaPixelCommand) => {
    if (typeof fbq.callMethod === 'function') {
      fbq.callMethod(...args);
      return;
    }

    fbq.queue = fbq.queue || [];
    fbq.queue.push(args);
  }) as MetaPixelFunction;

  // Mirror Meta's published bootstrap shape so helper tooling does not see
  // our pre-load stub as a second, conflicting implementation.
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  fbq.l = Date.now();

  return fbq;
};

const isMetaPixelHostAllowed = (hostname: string): boolean => {
  const normalizedHostname = hostname.trim().toLowerCase();
  if (!normalizedHostname) {
    return false;
  }

  return META_PIXEL_ALLOWED_HOSTS.has(normalizedHostname);
};

const normalizeMetaPixelBootstrap = (
  existingFbq: MetaPixelFunction
): MetaPixelFunction => {
  if (typeof existingFbq.callMethod === 'function') {
    return existingFbq;
  }

  const hasStandardQueueShape =
    existingFbq.push === existingFbq && Array.isArray(existingFbq.queue);
  if (hasStandardQueueShape) {
    if (!existingFbq.loaded) {
      existingFbq.loaded = true;
    }
    if (!existingFbq.version) {
      existingFbq.version = '2.0';
    }
    if (typeof existingFbq.l !== 'number') {
      existingFbq.l = Date.now();
    }

    return existingFbq;
  }

  const normalizedFbq = createMetaPixelQueueStub();
  const queuedCommands = Array.isArray(existingFbq.queue)
    ? existingFbq.queue
    : Array.isArray(existingFbq.q)
      ? existingFbq.q
      : [];

  normalizedFbq.queue = [...queuedCommands];

  if (typeof existingFbq.l === 'number') {
    normalizedFbq.l = existingFbq.l;
  }

  return normalizedFbq;
};

const ensureMetaPixelBootstrap = (): MetaPixelFunction => {
  if (typeof window.fbq === 'function') {
    const normalizedFbq = normalizeMetaPixelBootstrap(window.fbq);
    if (window.fbq !== normalizedFbq) {
      window.fbq = normalizedFbq;
    }
    if (!window._fbq || window._fbq !== normalizedFbq) {
      window._fbq = normalizedFbq;
    }

    return normalizedFbq;
  }

  const fbq = createMetaPixelQueueStub();
  window.fbq = fbq;

  if (!window._fbq) {
    window._fbq = fbq;
  }

  return fbq;
};

/**
 * Initializes the Meta Pixel (Facebook Pixel) on the page.
 * Loads the pixel script and initializes it with the provided pixel ID.
 * The pixel will automatically set the _fbp cookie which is used for CAPI matching.
 *
 * @param {string} pixelId - Meta Pixel ID from Events Manager
 * @returns {Promise<void>} Promise that resolves when pixel is initialized
 */
export const initializeMetaPixel = (pixelId: string): Promise<void> => {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  const currentHostname = window.location.hostname;

  // Skip Meta Pixel outside the production marketing hosts to avoid invalid-domain
  // cookie warnings on staging, previews, localhost, and other environments.
  if (!isMetaPixelHostAllowed(currentHostname)) {
    return Promise.resolve();
  }

  // Return early if pixel ID is not provided
  if (!pixelId || typeof pixelId !== 'string') {
    console.warn('Meta Pixel ID not provided, skipping initialization');
    return Promise.resolve();
  }

  // Check if pixel is already initialized (using a global flag)
  if (window._metaPixelInitialized) {
    return Promise.resolve();
  }

  // Check if script tag already exists to prevent duplicate loading
  const existingScript = document.querySelector(
    'script[src*="connect.facebook.net/en_US/fbevents.js"]'
  );
  if (existingScript) {
    // Another initializeMetaPixel call (e.g. the lean marketing entry and
    // main.tsx both scheduling init) already injected fbevents.js. Wait for it,
    // then defer to whichever caller initializes first.
    return new Promise(resolve => {
      // Check if already initialized
      if (window._metaPixelInitialized) {
        resolve();
        return;
      }
      // Wait for script to load and initialize
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max (50 * 100ms)
      const checkInterval = setInterval(() => {
        attempts++;
        // The injecting caller's onload may have initialized first — defer.
        if (window._metaPixelInitialized) {
          clearInterval(checkInterval);
          resolve();
          return;
        }
        // Only treat the pixel as *loaded* once the real fbevents.js installs
        // `callMethod`. The pre-load queue stub is also `typeof 'function'`, so
        // the old `typeof window.fbq === 'function'` test fired a second
        // init/PageView while the script was still downloading — a duplicate
        // PageView on slow connections. The stub never sets callMethod.
        if (typeof window.fbq?.callMethod === 'function') {
          // Real script loaded and nobody has initialized yet: do it once.
          try {
            window.fbq('init', pixelId);
            window.fbq('track', 'PageView');
            window._metaPixelInitialized = true;
          } catch (error) {
            console.warn('Meta Pixel failed to initialize:', error);
          }
          clearInterval(checkInterval);
          resolve();
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval);
          // Resolve instead of reject to not break app initialization
          console.warn('Meta Pixel script did not load within timeout');
          resolve();
        }
      }, 100);
    });
  }

  // Set up global error handler to catch Facebook Pixel errors
  if (!window._metaPixelErrorHandlerAdded) {
    window._metaPixelErrorHandlerAdded = true;
    const originalErrorHandler = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      // Suppress Facebook Pixel errors (they're non-critical)
      if (
        source &&
        typeof source === 'string' &&
        source.includes('connect.facebook.net')
      ) {
        // Silently ignore Facebook Pixel script errors
        return true; // Prevent default error handling
      }
      // Call original error handler for other errors
      if (originalErrorHandler) {
        try {
          return originalErrorHandler.call(
            window,
            message,
            source,
            lineno,
            colno,
            error
          );
        } catch (chainError) {
          console.warn('Chained window.onerror threw:', chainError);
          return false;
        }
      }
      return false;
    };

    // Also catch unhandled promise rejections from Facebook Pixel
    const originalUnhandledRejection = window.onunhandledrejection;
    window.onunhandledrejection = event => {
      try {
        const reason = event.reason as { stack?: unknown } | null | undefined;
        const stack =
          reason && typeof reason === 'object' ? reason.stack : null;
        if (
          typeof stack === 'string' &&
          stack.includes('connect.facebook.net')
        ) {
          event.preventDefault(); // Suppress the error
          return;
        }
      } catch {
        // Reason getter threw — fall through to chained handler.
      }
      if (originalUnhandledRejection) {
        try {
          originalUnhandledRejection.call(window, event);
        } catch (chainError) {
          console.warn(
            'Chained window.onunhandledrejection threw:',
            chainError
          );
        }
      }
    };
  }

  return new Promise(resolve => {
    try {
      // Initialize Meta's standard queue stub before the remote script loads.
      ensureMetaPixelBootstrap();

      // Create and inject the pixel script
      const script = document.createElement('script');
      script.async = true;
      script.setAttribute('data-cfasync', 'false');
      script.src = `https://connect.facebook.net/en_US/fbevents.js`;
      script.onload = () => {
        try {
          // Re-check: a concurrent initializeMetaPixel (lean entry + main.tsx)
          // may have initialized via the existing-script poller the instant the
          // script loaded. Guard so we don't fire a second init/PageView.
          if (!window._metaPixelInitialized) {
            // Initialize the pixel with the pixel ID
            window.fbq?.('init', pixelId);
            // Track PageView event
            window.fbq?.('track', 'PageView');
            window._metaPixelInitialized = true;
          }
          resolve();
        } catch (error) {
          console.warn(
            'Meta Pixel initialized but failed to track PageView:',
            error
          );
          // Still resolve as pixel is loaded
          window._metaPixelInitialized = true;
          resolve();
        }
      };
      script.onerror = error => {
        console.error('Failed to load Meta Pixel script:', error);
        // Resolve instead of reject to not break app initialization
        // The pixel just won't track events
        resolve();
      };

      // Insert script into document
      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        document.head.appendChild(script);
      }

      // Add noscript fallback for users with JavaScript disabled (only if not already exists)
      const existingNoscript = document.querySelector(
        `noscript img[src*="facebook.com/tr?id=${pixelId}"]`
      );
      if (!existingNoscript) {
        const noscript = document.createElement('noscript');
        const img = document.createElement('img');
        img.height = 1;
        img.width = 1;
        img.style.display = 'none';
        img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
        noscript.appendChild(img);
        document.body.appendChild(noscript);
      }
    } catch (error) {
      console.error('Error initializing Meta Pixel:', error);
      // Resolve instead of reject to not break app initialization
      // The pixel just won't track events
      resolve();
    }
  });
};

/**
 * Tracks a custom event with Meta Pixel.
 * Use this for tracking custom conversion events beyond PageView.
 *
 * @param {string} eventName - Name of the event to track
 * @param {Object} [eventData] - Optional event data object
 */
export const trackMetaEvent = (
  eventName: string,
  eventData: Record<string, unknown> = {}
): void => {
  if (typeof window === 'undefined' || !window.fbq) {
    console.warn('Meta Pixel not initialized, cannot track event:', eventName);
    return;
  }

  try {
    // Extract eventID from eventData and pass it as the options object (4th arg).
    // Meta's fbq API requires: fbq('track', eventName, customData, { eventID })
    // for deduplication between browser pixel and server CAPI events.
    const { eventID, ...customData } = eventData;
    if (eventID) {
      window.fbq('track', eventName, customData, { eventID });
    } else {
      window.fbq('track', eventName, customData);
    }
  } catch (error) {
    console.error('Error tracking Meta Pixel event:', error);
  }
};

/**
 * Checks if Meta Pixel is initialized and ready.
 *
 * @returns {boolean} True if pixel is initialized
 */
export const isMetaPixelInitialized = (): boolean => {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
};
